import practiceImage from "app/assets/practice-image.png";
import { PRACTICE_LEVEL } from "app/assets/practiceLevel";
import ErrorBoundary from "app/error/ErrorBoundary";
import GameDataProvider from "app/game-data/GameDataProvider";
import useGameData from "app/game-data/useGameData";
import useNewGame from "app/game-data/useNewGame";
import GameOver from "app/game-over/GameOver";
import Game from "app/game/Game";
import GameStateProvider from "app/game/GameStateProvider";
import useGameState, {
  GameAction,
  ONBOARDING_PHASE,
  useGameActionDispatch,
} from "app/game/useGameState";
import {
  GODOT_EVENT,
  GODOT_MESSAGE,
  sendGodotMessage,
  useGodotListener,
} from "app/godot-embed/godot-bridge";
import HowToPlay from "app/how-to-play/HowToPlay";
import TopNav from "app/top-nav/TopNav";
import Credits from "app/ui/Credits";
import Settings from "app/ui/Settings";
import TitleScreen from "app/ui/TitleScreen";
import cn from "app/util/classnames";
import { useGlobalReset } from "app/util/useResetableStorage";
import { GameConfigProvider } from "game-config";
import mixpanel from "mixpanel-browser";
import { useCallback, useEffect, useState } from "react";

import TutorialMessagePanel from "app/tutorial/TutorialMessagePanel";
import TutorialProvider from "app/tutorial/TutorialProvider";
import useTutorialState, {
  TutorialAction,
  useTutorialActionDispatch,
} from "app/tutorial/useTutorialState";
import VictoryPanel from "app/ui/VictoryPanel";
import { useImageDimensions } from "app/util/useImageDimensions";
import TutorialSelect from "./tutorial/TutorialSelect";
import OnboardingOverlays from "./tutorial/OnboardingOverlays";
import useTutorialLevel from "./tutorial/useTutorialLevel";

// Different overlays that can appear on top of the loaded Godot canvas.
const GAMEPLAY_OVERLAY = {
  NONE: "none",
  TUTORIAL_SELECT: "tutorialSelect",
  HOW_TO_PLAY: "howToPlay",
  SETTINGS: "settings",
  CREDITS: "credits",
  VICTORY_PANEL: "victoryPanel",
} as const;

type GameplayOverlay = (typeof GAMEPLAY_OVERLAY)[keyof typeof GAMEPLAY_OVERLAY];

function App() {
  // Check for new game data
  useNewGame();

  const [isTitleScreenDismissed, setIsTitleScreenDismissed] = useState(false);

  // True when the user has backed out of the results screen to view their
  // completed version of the level.
  const [isViewingCompletedGrid, setIsViewingCompletedGrid] = useState(false);

  const [gameplayOverlay, setGameplayOverlay] = useState<GameplayOverlay>(
    GAMEPLAY_OVERLAY.NONE,
  );

  const gameDispatch = useGameActionDispatch();

  const { start: startTutorialLevel, advancePhase: advanceTutorialLevel } =
    useTutorialLevel();

  const {
    id: game_id,
    isGameOver,
    gameTime,
    isDailyLevelStarted,
    onboardingPhase,
  } = useGameState();

  const {
    phaseData: tutorialPhase,
    levelData: tutorialLevelData,
    isInTutorial,
  } = useTutorialState();

  const tutorialDispatch = useTutorialActionDispatch();

  // Keep Godot in sync with the current tutorial phase
  useEffect(() => {
    if (isInTutorial) {
      sendGodotMessage(
        GODOT_MESSAGE.SET_TUTORIAL_PHASE,
        JSON.stringify(tutorialPhase),
      );
    }
  }, [isInTutorial, tutorialPhase]);

  // Determine what level to use. The old practice level is hardcoded,
  // so we use it as a fallback if game data could not be fetched.
  const gameData = useGameData();
  const levelLayout = JSON.stringify(
    isInTutorial && tutorialLevelData != null
      ? tutorialLevelData.gridLayout
      : gameData?.level == null
        ? PRACTICE_LEVEL
        : gameData?.level.puzzle,
  );

  // Store the dimensions of the TED screenshot image when it loads so that we
  // can pass it to the victory panel at the end of the game.
  const { width: tedImageWidth, height: tedImageHeight } = useImageDimensions(
    gameData?.level?.originalScreenshotUrl ?? "",
  );

  const handleTitleScreenDismiss = useCallback(() => {
    setIsTitleScreenDismissed(true);

    // If it's a new player, we will bring up the onboarding menus rather
    // that start the game.
    if (onboardingPhase !== ONBOARDING_PHASE.FINISHED) {
      return;
    }

    if (!isDailyLevelStarted) {
      mixpanel.track("game_start", { game_id });
      gameDispatch({ type: GameAction.DAILY_LEVEL_START });
    }
  }, [
    onboardingPhase,
    isDailyLevelStarted,
    gameDispatch,
    setIsTitleScreenDismissed,
    game_id,
  ]);

  // When How to Play is chosen from the game menu
  function handleHowToPlayClick() {
    setGameplayOverlay(GAMEPLAY_OVERLAY.HOW_TO_PLAY);
    mixpanel.track_pageview({
      game_id,
      page: `How to Play`,
    });
  }

  function handleHowToPlayDismiss() {
    setGameplayOverlay(GAMEPLAY_OVERLAY.NONE);

    // Otherwise, just start/resume the normal level
    if (!isDailyLevelStarted) {
      mixpanel.track("game_start", { game_id });
      gameDispatch({ type: GameAction.DAILY_LEVEL_START });
    }
  }

  function handleTutorialSelectClick() {
    setGameplayOverlay(GAMEPLAY_OVERLAY.TUTORIAL_SELECT);
  }

  function handleTutorialSelectLevelSelected() {
    setGameplayOverlay(GAMEPLAY_OVERLAY.NONE);
  }

  function handleTutorialSelectDismiss() {
    setGameplayOverlay(GAMEPLAY_OVERLAY.NONE);
  }

  function handleQuitTutorialClick() {
    setIsTitleScreenDismissed(false);
    tutorialDispatch({ type: TutorialAction.FINISH_TUTORIAL });
  }

  function handleOnboardingFinish() {
    setGameplayOverlay(GAMEPLAY_OVERLAY.NONE);
    if (!isDailyLevelStarted) {
      mixpanel.track("game_start", { game_id });
      gameDispatch({ type: GameAction.DAILY_LEVEL_START });
    }
  }

  const handleGameplayStart = useCallback(() => {
    if (isInTutorial && tutorialPhase != null) {
      const stringTutPhase = JSON.stringify(tutorialPhase);
      sendGodotMessage(GODOT_MESSAGE.SET_TUTORIAL_PHASE, stringTutPhase);
    }
  }, [isInTutorial, tutorialPhase]);
  useGodotListener(GODOT_EVENT.GAMEPLAY_START, handleGameplayStart);

  const handleGameWin = useCallback(() => {
    if (!isGameOver) {
      mixpanel.track("game_completed", { game_id, play_time: gameTime });
      setGameplayOverlay(GAMEPLAY_OVERLAY.VICTORY_PANEL);
    }
  }, [game_id, gameTime, isGameOver]);

  useGodotListener(GODOT_EVENT.VICTORY, handleGameWin);

  const onVictoryPanelDismiss = useCallback(() => {
    setGameplayOverlay(GAMEPLAY_OVERLAY.NONE);
    gameDispatch({ type: GameAction.GAME_OVER });
  }, [gameDispatch]);

  if (!isViewingCompletedGrid) {
    if (isGameOver) {
      return (
        <GameOver onViewCompletedGrid={() => setIsViewingCompletedGrid(true)} />
      );
    }

    if (!isTitleScreenDismissed) {
      return <TitleScreen onDismiss={handleTitleScreenDismiss} />;
    }
  }

  // Gameplay screen
  return (
    <div
      className={cn(
        "w-full h-dvh min-h-0",
        isInTutorial && tutorialPhase?.vignettes
          ? // #020b16 matches the color of the gameplay BG with the vignette overlay on top of it
            "bg-[#031123]"
          : "bg-gameplay-bg",
        "transition-colors duration-500 ease-linear",
        "flex flex-col overflow-auto",
        "relative",
        "overflow-clip",
      )}
    >
      <TopNav
        hideMenuButton={false}
        onHowToPlayClick={handleHowToPlayClick}
        onTutorialSelectClick={handleTutorialSelectClick}
        onSettingsClick={() => setGameplayOverlay(GAMEPLAY_OVERLAY.SETTINGS)}
        onCreditsClick={() => setGameplayOverlay(GAMEPLAY_OVERLAY.CREDITS)}
        onQuitTutorialClick={handleQuitTutorialClick}
        showGameTimer={
          gameplayOverlay === GAMEPLAY_OVERLAY.NONE &&
          !isViewingCompletedGrid &&
          !isInTutorial
        }
      />
      {isInTutorial && (
        <TutorialMessagePanel onContinue={advanceTutorialLevel} />
      )}
      <Game
        key={levelLayout.toString()}
        levelLayout={levelLayout}
        // Use the old practiceImage as a fallback since it's stored locally
        victoryImageUrl={gameData?.level.originalScreenshotUrl ?? practiceImage}
        saveBoardState={tutorialPhase == null}
        isAlreadyComplete={isViewingCompletedGrid}
        onBackButtonClick={() => setIsViewingCompletedGrid(false)}
      />
      {onboardingPhase !== ONBOARDING_PHASE.FINISHED && (
        <div
          className={cn(
            "absolute inset-0 z-10",
            isInTutorial && "pointer-events-none",
          )}
        >
          <OnboardingOverlays
            onTutorialLevelStarted={(level) => {
              setGameplayOverlay(GAMEPLAY_OVERLAY.NONE);
              startTutorialLevel(level);
            }}
            onTutorialFinished={handleOnboardingFinish}
          />
        </div>
      )}
      {gameplayOverlay !== GAMEPLAY_OVERLAY.NONE && (
        <div // Overlay container
          className="absolute inset-0 z-20"
        >
          {gameplayOverlay === GAMEPLAY_OVERLAY.TUTORIAL_SELECT && (
            <TutorialSelect
              onLevelSelected={handleTutorialSelectLevelSelected}
              onDismiss={handleTutorialSelectDismiss}
            />
          )}
          {gameplayOverlay === GAMEPLAY_OVERLAY.HOW_TO_PLAY && (
            <HowToPlay onDismiss={handleHowToPlayDismiss} />
          )}
          {gameplayOverlay === GAMEPLAY_OVERLAY.SETTINGS && (
            <Settings
              onClose={() => setGameplayOverlay(GAMEPLAY_OVERLAY.NONE)}
            />
          )}
          {gameplayOverlay === GAMEPLAY_OVERLAY.CREDITS && (
            <Credits
              onClose={() => setGameplayOverlay(GAMEPLAY_OVERLAY.NONE)}
            />
          )}
          {gameplayOverlay === GAMEPLAY_OVERLAY.VICTORY_PANEL && (
            <VictoryPanel
              tedImageName={gameData?.level?.name ?? ""}
              tedImageUrl={gameData?.level?.originalScreenshotUrl ?? ""}
              tedImageWidth={tedImageWidth}
              tedImageHeight={tedImageHeight}
              solvedBoardLayout={gameData?.level?.puzzle ?? ""}
              onDismiss={onVictoryPanelDismiss}
            />
          )}
        </div>
      )}
    </div>
  );
}

function WrappedApp() {
  useGlobalReset();

  return (
    <GameConfigProvider>
      <ErrorBoundary>
        <GameDataProvider>
          <GameStateProvider>
            <TutorialProvider>
              <App />
            </TutorialProvider>
          </GameStateProvider>
        </GameDataProvider>
      </ErrorBoundary>
    </GameConfigProvider>
  );
}

export default WrappedApp;
